/** Native observations are never replaced with guessed command results. All clocks injected for deterministic tests. */
export class NativeSync {
    port;
    intent;
    mono;
    serverNow;
    supportsRate;
    room = null;
    state = 'unknown';
    position = 0;
    observedAt = -Infinity;
    timeObservedAt = -Infinity;
    duration = 0;
    rate = 1;
    ready = false;
    ad = false;
    blocked = false;
    error = null;
    pending = null;
    quietUntil = 0;
    lastSeekAt = -Infinity;
    seekLatencyMs = 0;
    awaitingRoomUntil = 0;
    force = true;
    pausedLocally = false;
    pauseCandidateAt = null;
    seekCandidate = null;
    connected = false;
    canControl = false;
    autoCatchUp = true;
    constructor(port, intent, mono = () => performance.now(), serverNow = () => Date.now(), supportsRate = false) {
        this.port = port;
        this.intent = intent;
        this.mono = mono;
        this.serverNow = serverNow;
        this.supportsRate = supportsRate;
    }
    configure(connected, canControl, autoCatchUp = true) {
        if (!this.connected && connected) {
            this.force = true;
            this.pending = null;
            this.quietUntil = 0;
        }
        if (!connected) {
            this.pending = null;
            this.seekCandidate = null;
            this.pauseCandidateAt = null;
            this.awaitingRoomUntil = 0;
        }
        this.connected = connected;
        this.canControl = canControl;
        this.autoCatchUp = autoCatchUp;
    }
    receive(next) {
        if (this.room?.epoch === next.epoch && (next.revision ?? 0) < (this.room?.revision ?? 0))
            return;
        const changed = !this.room || this.room.epoch !== next.epoch || this.room.revision !== next.revision ||
            this.room.status !== next.status || this.room.anchorServerMs !== next.anchorServerMs;
        if (changed) {
            this.pending = null;
            this.awaitingRoomUntil = 0;
            this.pauseCandidateAt = null;
            this.seekCandidate = null;
            this.force = true;
            this.lastSeekAt = -Infinity;
            // Native events already in flight belong to the old timeline.
            this.quietUntil = this.room ? this.mono() + 700 : 0;
            this.pausedLocally = false;
        }
        this.room = next;
    }
    observe(event) {
        const now = this.mono();
        const previous = this.state;
        const expected = this.estimate(now);
        if (event.kind === 'error') {
            this.error = event.error || 'Площадка не смогла открыть видео';
            return;
        }
        if (event.kind === 'ready')
            this.ready = true;
        if (event.kind === 'blocked') {
            this.blocked = true;
            this.pending = null;
            return;
        }
        if (event.ad != null) {
            this.ad = event.ad;
            this.pending = null;
            this.pauseCandidateAt = null;
            this.seekCandidate = null;
            this.quietUntil = now + 1500;
            this.force = true;
        }
        if (Number.isFinite(event.durationMs) && event.durationMs > 0 && !this.ad)
            this.duration = event.durationMs;
        if (event.state) {
            this.position = expected;
            this.observedAt = now;
            this.state = event.state;
        }
        const hasTime = Number.isFinite(event.positionMs) && event.positionMs >= 0;
        const hadTime = Number.isFinite(this.timeObservedAt);
        if (hasTime) {
            this.position = event.positionMs;
            this.observedAt = now;
            this.timeObservedAt = now;
            this.ready = true;
        }
        if (event.rate && Number.isFinite(event.rate))
            this.rate = event.rate;
        if (this.state === 'playing') {
            this.blocked = false;
            this.pausedLocally = false;
        }
        if (previous === 'buffering' && this.state !== 'buffering') {
            // A stalled provider must get a fresh timeout after it resumes.
            if (this.pending)
                this.pending.sent = now;
            this.force = true;
        }
        const pending = this.pending;
        if (pending) {
            const seekArrived = hasTime && (event.kind === 'seek' || Math.abs(this.position - expected) > 350 || this.state !== 'playing');
            const confirmed = pending.action === 'seek' ? seekArrived && (Math.abs(this.position - pending.value) < 1200 || this.state === 'playing' && Math.abs(this.position - pending.value - Math.max(0, now - pending.sent) * this.rate) < 1200) :
                pending.action === 'play' ? event.state === 'playing' : pending.action === 'pause' ? event.state === 'paused' || event.state === 'ended' :
                    event.rate != null && Math.abs(event.rate - pending.value) < .01;
            if (confirmed) {
                if (pending.action === 'seek' && this.state === 'playing' && this.room?.status === 'playing') {
                    // Separate delayed application from delayed telemetry: playback already
                    // advanced since the seek means the provider applied it earlier.
                    this.seekLatencyMs = Math.min(3000, Math.max(0, now - pending.issuedAt - Math.max(0, this.position - pending.value) / this.rate));
                }
                this.pending = null;
                this.quietUntil = now + 900;
            }
            this.seekCandidate = null;
            return; // A command acknowledgement is never a new user action.
        }
        if (!this.connected || !this.room || this.ad || now < this.quietUntil || now < this.awaitingRoomUntil)
            return;
        if (this.state === 'buffering') {
            this.pauseCandidateAt = null;
            if (hasTime && hadTime && (event.kind === 'seek' || Math.abs(this.position - expected) > 1800) && now - this.lastSeekAt > 5000) {
                this.seekCandidate = { position: this.position, at: now, playing: false };
            }
            return;
        }
        if (previous === 'buffering' && this.seekCandidate && hasTime) {
            const candidate = this.seekCandidate;
            this.seekCandidate = null;
            if (Math.abs(this.position - candidate.position) < 900 && Math.abs(this.position - this.target()) > 1400) {
                this.sendIntent('seek');
                return;
            }
        }
        if (event.state === 'playing' && previous !== 'playing') {
            this.pauseCandidateAt = null;
            if (this.room.status === 'paused')
                this.sendIntent('play');
            else
                this.force = true; // Late viewer unlocked their own player; don't rewind the room.
            return;
        }
        if (event.state === 'paused' && previous === 'playing' && this.room.status === 'playing') {
            if (!this.canControl)
                this.pausedLocally = true;
            else
                this.pauseCandidateAt = now;
        }
        if (event.state === 'ended' && this.room.status === 'playing' && this.canControl)
            this.sendIntent('pause');
        if (event.kind === 'rate' && this.canControl && this.supportsRate && Math.abs(this.rate - this.room.rate) > .01)
            this.sendIntent('rate');
        if (hasTime && hadTime && this.state !== 'unknown') {
            if (event.kind === 'seek' && Math.abs(this.position - this.target()) > 1400) {
                this.sendIntent('seek');
                this.seekCandidate = null;
            }
            else if (this.seekCandidate) {
                const predicted = this.seekCandidate.position + (this.seekCandidate.playing ? (now - this.seekCandidate.at) * this.rate : 0);
                if (Math.abs(this.position - predicted) < 900 && now - this.seekCandidate.at < 2500)
                    this.sendIntent('seek');
                this.seekCandidate = null;
            }
            else if (Math.abs(this.position - expected) > 1800 && now - this.lastSeekAt > 5000) {
                this.seekCandidate = { position: this.position, at: now, playing: this.state === 'playing' };
            }
        }
    }
    target() {
        if (!this.room)
            return 0;
        let target = this.room.positionMs + (this.room.status === 'playing' ? Math.max(0, this.serverNow() - this.room.anchorServerMs) * this.room.rate : 0);
        if (this.duration > 0)
            target = Math.min(target, Math.max(0, this.duration - 200));
        return target;
    }
    sendIntent(action) {
        if (!this.canControl || !this.connected)
            return;
        this.awaitingRoomUntil = this.mono() + 3000;
        this.pauseCandidateAt = null;
        this.intent(action, this.estimate(this.mono()), action === 'rate' ? this.rate : undefined);
    }
    estimate(now) {
        return this.position + (this.state === 'playing' && !this.ad ? Math.min(1500, Math.max(0, now - this.observedAt)) * this.rate : 0);
    }
    retry() { this.blocked = false; this.error = null; this.pending = null; this.force = true; this.pausedLocally = false; this.lastSeekAt = -Infinity; }
    issue(action, value = 0) {
        this.pending = { action, value, sent: this.mono(), issuedAt: this.mono(), attempts: 1 };
        if (action === 'seek')
            this.lastSeekAt = this.mono();
        this.port.command(action, value);
    }
    tick() {
        const now = this.mono();
        const positionMs = this.estimate(now);
        const base = { positionMs, durationMs: this.duration, driftMs: null, error: this.error };
        const status = (phase) => ({ ...base, phase });
        if (this.error)
            return status('error');
        if (!this.connected)
            return status('offline');
        if (!this.ready || !this.room)
            return status('connecting');
        if (this.ad)
            return status('advertisement');
        if (this.pausedLocally)
            return status('local-pause');
        if (this.blocked)
            return status('waiting');
        if (this.state === 'buffering')
            return status('buffering');
        if (now - this.timeObservedAt > 4000 && this.state === 'playing')
            return status('manual');
        if (this.pauseCandidateAt !== null) {
            if (now - this.pauseCandidateAt >= 400)
                this.sendIntent('pause');
            return status('syncing');
        }
        if (this.seekCandidate && now - this.seekCandidate.at >= 2500)
            this.seekCandidate = null;
        if (now < this.awaitingRoomUntil || this.seekCandidate)
            return status('syncing');
        const target = this.target();
        base.driftMs = positionMs - target;
        if (this.pending) {
            if (now - this.pending.sent < 4000)
                return status('syncing');
            if (this.pending.action === 'play') {
                this.blocked = true;
                this.pending = null;
                return status('waiting');
            }
            if (this.pending.attempts >= 2) {
                this.error = 'Площадка не подтвердила команду. Повторите синхронизацию.';
                this.pending = null;
                return { ...status('manual'), error: this.error };
            }
            this.pending.attempts++;
            this.pending.sent = now;
            this.pending.issuedAt = now;
            this.port.command(this.pending.action, this.pending.value);
            return status('syncing');
        }
        if (this.room.status === 'paused' && this.state === 'playing') {
            this.issue('pause');
            return status('syncing');
        }
        if (this.state === 'ended' && this.duration > 0 && target >= this.duration - 250)
            return status('synced');
        // Unlock autoplay first. Seeking a blocked iframe repeatedly causes reload loops.
        if (this.room.status === 'playing' && this.state !== 'playing') {
            this.issue('play');
            return status('syncing');
        }
        if (this.supportsRate && Math.abs(this.rate - this.room.rate) > .01) {
            this.issue('rate', this.room.rate);
            return status('syncing');
        }
        // Never seek using an invented zero before the first timestamp arrives.
        if (!Number.isFinite(this.timeObservedAt))
            return status('connecting');
        const gap = Math.abs(base.driftMs);
        if ((this.force || this.autoCatchUp) && gap > (this.force ? 700 : 1400) && now - this.lastSeekAt > 5000) {
            const projected = target + (this.room.status === 'playing' ? this.seekLatencyMs * this.room.rate : 0);
            this.force = false;
            this.issue('seek', this.duration > 0 ? Math.min(projected, Math.max(0, this.duration - 200)) : projected);
            return status('syncing');
        }
        this.force = false;
        return status(gap <= 1400 ? 'synced' : 'manual');
    }
}
//# sourceMappingURL=sync.js.map